'use client';

import { c as _c } from "react/compiler-runtime";
import { MAX_SKILLS_PER_SESSION, opensSkillMenu, type UsedSkill } from '@shared/all/domains/skills';
import { type ResearchFile, type ResearchProgress, type ResearchProgressSource } from '@shared/all/domains/socket';
import toast from '@shared/utils/helpers/toast';
import useSkillPicks from '@shared/utils/hooks/useSkillPicks';
import useSocket from '@shared/utils/hooks/useSocket';
import useUser from '@shared/utils/hooks/useUser';
import { BookText, Cloud, Code, FlaskConical, Globe, HardDrive, Landmark, PenLine, ScrollText, Send, Sparkles, Youtube } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useReducer, useRef } from 'react';
import useContentUIRenderer from '../../hooks/useContentUIRenderer';
import { client } from '../../utils/trpc';
import Button from '../common/Button';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
import { trackActivation } from '../onboarding/trackOnboarding';
import ResearchFileCard from './ResearchFileCard';
import { SkillChips, SkillMenu, SkillsButton } from './SkillMenu';
type Msg = {
  role: 'user' | 'assistant';
  text: string;
  cost?: number;
  // The skills the session had followed by this answer (.specs/skills.md).
  skills?: UsedSkill[];
  // Files the assistant wrote into the user's vault with this answer.
  files?: ResearchFile[];
};
type Step = {
  source: ResearchProgressSource;
  label: string;
};
const SOURCE_ICON: Record<ResearchProgressSource, React.ReactNode> = {
  wikipedia: <BookText className="size-4" />,
  bluesky: <Cloud className="size-4" />,
  youtube: <Youtube className="size-4" />,
  openalex: <FlaskConical className="size-4" />,
  edgar: <Landmark className="size-4" />,
  onedrive: <HardDrive className="size-4" />,
  skill: <ScrollText className="size-4" />,
  code: <Code className="size-4" />,
  web: <Globe className="size-4" />
};
const SOURCE_COLOR: Record<ResearchProgressSource, string> = {
  wikipedia: 'text-textPrimary',
  bluesky: 'text-sky-500',
  youtube: 'text-red-500',
  openalex: 'text-amber-500',
  edgar: 'text-emerald-600',
  onedrive: 'text-sky-600',
  skill: 'text-violet-500',
  code: 'text-orange-500',
  web: 'text-primary'
};
type State = {
  sessionId: string | null;
  messages: Msg[];
  steps: Step[]; // live tool activity for the in-flight turn
  input: string;
  // The topic actually researched — equals the ?q query, or, when the search
  // box was empty, whatever the user typed on the start screen.
  topic: string;
  // select = start screen; starting = initial research; thinking = a turn;
  // posting = composing+publishing; composing = premium draft being written;
  // editing = premium draft open for edits before posting as self
  phase: 'select' | 'starting' | 'idle' | 'thinking' | 'posting' | 'composing' | 'editing' | 'posted';
  error: string | null;
  // The error is a balance under the research floor, so it gets a buy link.
  needsTrons: boolean;
  postUuid: string | null;
  postCost: number | null;
  premium: boolean;
  draft: {
    title: string;
    body: string;
  } | null;
  draftCost: number;
  // SkillMenu is open over the input.
  skillMenuOpen: boolean;
};
type Action = {
  type: 'START';
  topic: string;
} | {
  type: 'START_ACK';
  sessionId: string;
} | {
  type: 'SEND';
  text: string;
} | {
  type: 'TURN_DONE';
  sessionId?: string;
  text: string;
  cost: number;
  skills?: UsedSkill[];
  files?: ResearchFile[];
} | {
  type: 'PROGRESS';
  step: Step;
} | {
  type: 'POSTING';
} | {
  type: 'POSTED';
  uuid: string;
  cost: number;
} | {
  type: 'ERROR';
  sessionId?: string;
  message: string;
  needsTrons?: boolean;
} | {
  type: 'INPUT';
  value: string;
} | {
  type: 'PREMIUM';
  premium: boolean;
} | {
  type: 'COMPOSING';
} | {
  type: 'DRAFT_READY';
  title: string;
  body: string;
  cost: number;
} | {
  type: 'EDIT_DRAFT';
  field: 'title' | 'body';
  value: string;
} | {
  type: 'CANCEL_EDIT';
} | {
  type: 'SKILL_MENU';
  open: boolean;
};
const initialState: State = {
  sessionId: null,
  messages: [],
  steps: [],
  input: '',
  topic: '',
  phase: 'select',
  error: null,
  needsTrons: false,
  postUuid: null,
  postCost: null,
  premium: false,
  draft: null,
  draftCost: 0,
  skillMenuOpen: false
};
const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'START':
      // Lock in the researched topic and clear the input so the follow-up
      // box (which reuses `input`) doesn't start out pre-filled with it.
      return {
        ...state,
        phase: 'starting',
        error: null,
        steps: [],
        topic: action.topic,
        input: ''
      };
    case 'START_ACK':
      // Session created; the turn runs on the server and its result arrives
      // over the socket (TURN_DONE). Stay in 'starting' until then.
      return {
        ...state,
        sessionId: action.sessionId
      };
    case 'SEND':
      return {
        ...state,
        phase: 'thinking',
        error: null,
        input: '',
        steps: [],
        messages: [...state.messages, {
          role: 'user',
          text: action.text
        }]
      };
    case 'TURN_DONE':
      // Socket result — ignore anything not for the panel's current session.
      if (action.sessionId && state.sessionId && action.sessionId !== state.sessionId) return state;
      return {
        ...state,
        phase: 'idle',
        steps: [],
        messages: [...state.messages, {
          role: 'assistant',
          text: action.text,
          cost: action.cost,
          ...(action.skills?.length ? {
            skills: action.skills
          } : {}),
          ...(action.files?.length ? {
            files: action.files
          } : {})
        }]
      };
    case 'PROGRESS':
      // Only collect steps while a turn is actually in flight.
      if (state.phase !== 'starting' && state.phase !== 'thinking') return state;
      return {
        ...state,
        steps: [...state.steps, action.step]
      };
    case 'POSTING':
      return {
        ...state,
        phase: 'posting',
        error: null
      };
    case 'POSTED':
      return {
        ...state,
        phase: 'posted',
        postUuid: action.uuid,
        postCost: action.cost
      };
    case 'ERROR':
      if (action.sessionId && state.sessionId && action.sessionId !== state.sessionId) return state;
      return {
        ...state,
        phase: state.sessionId ? 'idle' : 'select',
        steps: [],
        error: action.message,
        needsTrons: action.needsTrons ?? false
      };
    case 'INPUT':
      return {
        ...state,
        input: action.value
      };
    case 'PREMIUM':
      return {
        ...state,
        premium: action.premium
      };
    case 'COMPOSING':
      return {
        ...state,
        phase: 'composing',
        error: null
      };
    case 'DRAFT_READY':
      return {
        ...state,
        phase: 'editing',
        draft: {
          title: action.title,
          body: action.body
        },
        draftCost: state.draftCost + action.cost
      };
    case 'EDIT_DRAFT':
      if (!state.draft) return state;
      return {
        ...state,
        draft: {
          ...state.draft,
          [action.field]: action.value
        }
      };
    case 'CANCEL_EDIT':
      return {
        ...state,
        phase: 'idle'
      };
    case 'SKILL_MENU':
      return {
        ...state,
        skillMenuOpen: action.open
      };
  }
};

// User = plain text; assistant = rendered HTML (passed in as `children`).
const Bubble = t0 => {
  const $ = _c(6);
  const {
    role,
    children
  } = t0;
  const t1 = `flex ${role === "user" ? "justify-end" : "justify-start"}`;
  const t2 = `max-w-[85%] rounded-2xl px-4 py-2.5 text-[14px] leading-relaxed ${role === "user" ? "whitespace-pre-wrap bg-primary text-white" : "bg-background-light text-textPrimary"}`;
  let t3;
  if ($[0] !== children || $[1] !== t2) {
    t3 = <div className={t2}>{children}</div>;
    $[0] = children;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t1 || $[4] !== t3) {
    t4 = <div className={t1}>{t3}</div>;
    $[3] = t1;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
const Dots = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="inline-flex items-center gap-1"><span className="size-1.5 animate-bounce rounded-full bg-textSecondary/50 [animation-delay:-0.3s]" /><span className="size-1.5 animate-bounce rounded-full bg-textSecondary/50 [animation-delay:-0.15s]" /><span className="size-1.5 animate-bounce rounded-full bg-textSecondary/50" /></span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

// Live tool activity, newest step pulsing as in-progress.
const Activity = t0 => {
  const $ = _c(4);
  const {
    steps
  } = t0;
  let t1;
  if ($[0] !== steps) {
    t1 = steps.length === 0 ? <div className="flex items-center gap-2 text-sm text-textSecondary"><Globe className="size-4 animate-pulse text-primary" /><span>Planning research</span><Dots /></div> : steps.map((step, i) => {
      const active = i === steps.length - 1;
      return <div key={i} className={`flex items-center gap-2 text-sm transition-opacity ${active ? "text-textPrimary" : "text-textSecondary opacity-70"}`}><span className={active ? `${SOURCE_COLOR[step.source]} animate-pulse` : SOURCE_COLOR[step.source]}>{SOURCE_ICON[step.source]}</span><span className="flex-1 truncate">{step.label}</span>{active ? <Dots /> : <span className="text-primary">✓</span>}</div>;
    });
    $[0] = steps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="flex justify-start"><div className="min-w-[60%] space-y-2 rounded-2xl bg-background-light px-4 py-3">{t1}</div></div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const ResearchPanel = t0 => {
  const $ = _c(158);
  const {
    query,
    closeHandler,
    inline: t1,
    hideHeader: t2,
    source: t3
  } = t0;
  const inline = t1 === undefined ? false : t1;
  const hideHeader = t2 === undefined ? false : t2;
  const source = t3 === undefined ? "web" : t3;
  const [state, dispatch] = useReducer(reducer, query, _temp);
  const {
    onResearchProgress,
    onResearchResult,
    onResearchError
  } = useSocket();
  const {
    contentUIRenderHandler
  } = useContentUIRenderer();
  const {
    currentUser
  } = useUser();
  const bottomRef = useRef(null);
  const skillPicks = useSkillPicks();
  const skillAnchor = useRef(null);
  let t4;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      let ignore = false;
      client.users.premium.query(undefined, {
        context: {
          silenceErrors: true
        }
      }).then(t6 => {
        const [err, data] = t6;
        if (ignore || err || !data) {
          return;
        }
        const active = data.premiumExpires != null && new Date(data.premiumExpires) > new Date();
        dispatch({
          type: "PREMIUM",
          premium: active
        });
      });
      return () => {
        ignore = true;
      };
    };
    t5 = [];
    $[0] = t4;
    $[1] = t5;
  } else {
    t4 = $[0];
    t5 = $[1];
  }
  useEffect(t4, t5);
  let t6;
  if ($[2] !== onResearchError || $[3] !== onResearchProgress || $[4] !== onResearchResult) {
    t6 = () => {
      const offProgress = onResearchProgress(payload => {
        dispatch({
          type: "PROGRESS",
          step: {
            source: payload.source,
            label: payload.label
          }
        });
      });
      const offResult = onResearchResult(payload_0 => {
        dispatch({
          type: "TURN_DONE",
          sessionId: payload_0.sessionId,
          text: payload_0.assistantMessage,
          cost: payload_0.cost,
          skills: payload_0.skills,
          files: payload_0.files
        });
      });
      const offError = onResearchError(payload_1 => {
        dispatch({
          type: "ERROR",
          sessionId: payload_1.sessionId,
          message: payload_1.message
        });
      });
      return () => {
        offProgress();
        offResult();
        offError();
      };
    };
    $[2] = onResearchError;
    $[3] = onResearchProgress;
    $[4] = onResearchResult;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  useEffect(t6, t7);
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      bottomRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end"
      });
    };
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== state.messages.length || $[9] !== state.phase || $[10] !== state.steps.length) {
    t9 = [state.messages.length, state.steps.length, state.phase];
    $[8] = state.messages.length;
    $[9] = state.phase;
    $[10] = state.steps.length;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  useEffect(t8, t9);
  let t10;
  if ($[12] !== closeHandler || $[13] !== state.phase || $[14] !== state.sessionId) {
    t10 = () => {
      if (state.sessionId && state.phase !== "posted") {
        client.research.abandon.mutate({
          sessionId: state.sessionId
        }, {
          context: {
            silenceErrors: true
          }
        });
      }
      closeHandler();
    };
    $[12] = closeHandler;
    $[13] = state.phase;
    $[14] = state.sessionId;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const handleClose = t10;
  let t11;
  if ($[16] !== currentUser || $[17] !== skillPicks || $[18] !== source || $[19] !== state.input) {
    t11 = async () => {
      const topic = state.input.trim();
      if (!topic) {
        return;
      }
      const skillUuids = skillPicks.picks.map(_temp2);
      dispatch({
        type: "START",
        topic
      });
      const [err_0, data_0] = await client.research.start.mutate({
        query: topic,
        source,
        skillUuids
      }, {
        context: {
          silenceErrors: true
        }
      });
      if (err_0 || !data_0) {
        dispatch({
          type: "ERROR",
          message: err_0?.message || "Research failed to start.",
          needsTrons: err_0?.data?.code === "PAYMENT_REQUIRED"
        });
        return;
      }
      dispatch({
        type: "START_ACK",
        sessionId: data_0.sessionId
      });
      skillPicks.clear();
      trackActivation(currentUser.onboardingPath, "research");
    };
    $[16] = currentUser;
    $[17] = skillPicks;
    $[18] = source;
    $[19] = state.input;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const start = t11;
  let t12;
  if ($[21] !== skillPicks || $[22] !== state.input || $[23] !== state.phase || $[24] !== state.sessionId) {
    t12 = async () => {
      const text = state.input.trim();
      if (!text || !state.sessionId || state.phase === "thinking") {
        return;
      }
      const skillUuids_0 = skillPicks.picks.map(_temp3);
      dispatch({
        type: "SEND",
        text
      });
      const [err_1] = await client.research.reply.mutate({
        sessionId: state.sessionId,
        message: text,
        skillUuids: skillUuids_0
      }, {
        context: {
          silenceErrors: true
        }
      });
      if (err_1) {
        dispatch({
          type: "ERROR",
          message: err_1.message || "Reply failed.",
          needsTrons: err_1.data?.code === "PAYMENT_REQUIRED"
        });
        return;
      }
      skillPicks.clear();
    };
    $[21] = skillPicks;
    $[22] = state.input;
    $[23] = state.phase;
    $[24] = state.sessionId;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const send = t12;
  let t13;
  if ($[26] !== state.sessionId) {
    t13 = async () => {
      if (!state.sessionId) {
        return;
      }
      dispatch({
        type: "POSTING"
      });
      const [err_2, data_1] = await client.research.confirm.mutate({
        sessionId: state.sessionId
      }, {
        context: {
          silenceErrors: true
        }
      });
      if (err_2 || !data_1) {
        dispatch({
          type: "ERROR",
          message: err_2?.message || "Could not post."
        });
        return;
      }
      dispatch({
        type: "POSTED",
        uuid: data_1.questionUuid ?? data_1.postUuid,
        cost: data_1.cost
      });
    };
    $[26] = state.sessionId;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const confirm = t13;
  let t14;
  if ($[28] !== state.draft || $[29] !== state.sessionId) {
    t14 = async () => {
      if (!state.sessionId) {
        return;
      }
      if (state.draft) {
        dispatch({
          type: "DRAFT_READY",
          title: state.draft.title,
          body: state.draft.body,
          cost: 0
        });
        return;
      }
      dispatch({
        type: "COMPOSING"
      });
      const [err_3, data_2] = await client.research.composeDraft.mutate({
        sessionId: state.sessionId
      }, {
        context: {
          silenceErrors: true
        }
      });
      if (err_3 || !data_2) {
        dispatch({
          type: "ERROR",
          message: err_3?.message || "Could not compose the draft."
        });
        return;
      }
      dispatch({
        type: "DRAFT_READY",
        title: data_2.draft.title,
        body: data_2.draft.body,
        cost: data_2.cost
      });
    };
    $[28] = state.draft;
    $[29] = state.sessionId;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const composeDraft = t14;
  let t15;
  if ($[31] !== state.draft || $[32] !== state.sessionId) {
    t15 = async () => {
      if (!state.sessionId || !state.draft?.body.trim()) {
        return;
      }
      dispatch({
        type: "POSTING"
      });
      const [err_4, data_3] = await client.research.confirm.mutate({
        sessionId: state.sessionId,
        asSelf: true,
        title: state.draft.title,
        body: state.draft.body
      }, {
        context: {
          silenceErrors: true
        }
      });
      if (err_4 || !data_3) {
        dispatch({
          type: "ERROR",
          message: err_4?.message || "Could not post."
        });
        return;
      }
      dispatch({
        type: "POSTED",
        uuid: data_3.questionUuid ?? data_3.postUuid,
        cost: data_3.cost
      });
    };
    $[31] = state.draft;
    $[32] = state.sessionId;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const confirmAsSelf = t15;
  const vault = source === "vault";
  const busy = state.phase === "thinking" || state.phase === "posting" || state.phase === "composing";
  const totalCost = state.messages.reduce(_temp4, 0) + state.draftCost + (state.postCost ?? 0);
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Sparkles className="size-5 text-primary" />;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const t17 = vault ? "Ask OneDrive" : "Research with AI";
  let t18;
  if ($[35] !== t17) {
    t18 = <h2 className="text-lg font-semibold">{t17}</h2>;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== totalCost) {
    t19 = totalCost > 0 && <span className="ml-auto rounded-full bg-background-light px-2.5 py-1 text-xs font-emphasis text-textSecondary">{totalCost.toLocaleString()} Trons</span>;
    $[37] = totalCost;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t18 || $[40] !== t19) {
    t20 = <div className="flex w-full items-center gap-2">{t16}{t18}{t19}</div>;
    $[39] = t18;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  const headerInner = t20;
  let t21;
  if ($[42] !== skillPicks) {
    t21 = skill => {
      dispatch({
        type: "SKILL_MENU",
        open: false
      });
      if (!skillPicks.toggle(skill)) {
        toast(`That is ${MAX_SKILLS_PER_SESSION} skills, as many as one message takes.`, "Remove one to pick another.", "error");
      }
    };
    $[42] = skillPicks;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  const toggleSkill = t21;
  let t22;
  if ($[44] !== state.input) {
    t22 = next => {
      if (opensSkillMenu(state.input, next)) {
        dispatch({
          type: "INPUT",
          value: next.slice(0, -1)
        });
        dispatch({
          type: "SKILL_MENU",
          open: true
        });
        return;
      }
      dispatch({
        type: "INPUT",
        value: next
      });
    };
    $[44] = state.input;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  const onInput = t22;
  let t23;
  if ($[46] !== skillPicks.picks || $[47] !== state.skillMenuOpen || $[48] !== toggleSkill) {
    t23 = state.skillMenuOpen && <SkillMenu anchor={skillAnchor} picks={skillPicks.picks} onToggle={toggleSkill} onClose={() => dispatch({
      type: "SKILL_MENU",
      open: false
    })} />;
    $[46] = skillPicks.picks;
    $[47] = state.skillMenuOpen;
    $[48] = toggleSkill;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  const skillMenu = t23;
  let t24;
  if ($[50] !== state.skillMenuOpen) {
    t24 = () => dispatch({
      type: "SKILL_MENU",
      open: !state.skillMenuOpen
    });
    $[50] = state.skillMenuOpen;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== skillPicks.picks.length || $[53] !== state.skillMenuOpen || $[54] !== t24) {
    t25 = <SkillsButton count={skillPicks.picks.length} open={state.skillMenuOpen} onPress={t24} />;
    $[52] = skillPicks.picks.length;
    $[53] = state.skillMenuOpen;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  const skillsButton = t25;
  let t26;
  if ($[56] !== skillPicks.picks || $[57] !== skillPicks.remove) {
    t26 = <SkillChips picks={skillPicks.picks} onRemove={skillPicks.remove} />;
    $[56] = skillPicks.picks;
    $[57] = skillPicks.remove;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  const skillChips = t26;
  let t27;
  if ($[59] !== onInput || $[60] !== skillChips || $[61] !== skillMenu || $[62] !== skillsButton || $[63] !== start || $[64] !== state.error || $[65] !== state.input || $[66] !== state.phase || $[67] !== vault) {
    t27 = state.phase === "select" && !state.error && <div className="flex min-h-[calc(min(32rem,90vh-20rem)-2rem)] flex-col items-center justify-center px-2 text-center"><div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 text-primary ring-1 ring-primary/15"><Sparkles className="size-7" strokeWidth={1.75} /></div><h3 className="text-base font-semibold text-textPrimary">{vault ? "Ask your OneDrive files" : "What do you want to research?"}</h3><p className="mx-auto mt-1.5 max-w-sm text-sm leading-relaxed text-textSecondary">{vault ? "The AI reads only the files you synced from OneDrive, cites the ones it used, and can save the answer as a private post." : "The AI searches the web for reputable sources, then writes a summary you can post so this search returns results next time."}</p><div className="mt-5 w-full max-w-sm text-left">{skillChips}</div><div ref={skillAnchor} className="relative mt-2 flex w-full max-w-sm items-center gap-2">{skillMenu}<input value={state.input} onChange={e => onInput(e.target.value)} onKeyDown={e_0 => {
          if (e_0.key === "Enter" && state.input.trim()) {
            e_0.preventDefault();
            start();
          }
        }} autoFocus={true} maxLength={200} placeholder={vault ? "e.g. what was the total on my invoice? (/ for skills)" : "e.g. how do heat pumps work? (/ for skills)"} className="bg-background min-w-0 flex-1 rounded-full border border-ds-border-standard px-4 py-2.5 text-sm text-textPrimary outline-none focus:border-primary" />{skillsButton}</div><div className="mt-5 flex justify-center"><Button variant="PRIMARY" text="Start research" onClick={() => void start()} disabled={!state.input.trim()} /></div></div>;
    $[59] = onInput;
    $[60] = skillChips;
    $[61] = skillMenu;
    $[62] = skillsButton;
    $[63] = start;
    $[64] = state.error;
    $[65] = state.input;
    $[66] = state.phase;
    $[67] = vault;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== state.error || $[70] !== state.phase || $[71] !== state.topic) {
    t28 = state.phase === "starting" && !state.error && <p className="text-sm text-textSecondary">Researching{" "}<span className="font-emphasis text-textPrimary">“{state.topic}”</span>…</p>;
    $[69] = state.error;
    $[70] = state.phase;
    $[71] = state.topic;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  let t29;
  if ($[73] !== contentUIRenderHandler || $[74] !== state.messages || $[75] !== state.sessionId) {
    let t30;
    if ($[77] !== contentUIRenderHandler || $[78] !== state.sessionId) {
      t30 = (msg, i) => <div key={i} className="space-y-0.5"><Bubble role={msg.role}>{msg.role === "assistant" ? contentUIRenderHandler(msg.text, `research-${i}`, true, {
            hideLinkPreview: true
          }) : msg.text}</Bubble>{msg.role === "assistant" && msg.skills && <div className="pl-1 text-[11px] text-textSecondary">Skills · {msg.skills.map(_temp5).join(", ")}</div>}{msg.files?.map(file => <ResearchFileCard key={`${file.id}-${file.status}`} file={file} sessionId={state.sessionId} />)}{msg.role === "assistant" && msg.cost != null && <div className="pl-1 text-[11px] text-textSecondary">{msg.cost.toLocaleString()} Trons</div>}</div>;
      $[77] = contentUIRenderHandler;
      $[78] = state.sessionId;
      $[79] = t30;
    } else {
      t30 = $[79];
    }
    t29 = state.messages.map(t30);
    $[73] = contentUIRenderHandler;
    $[74] = state.messages;
    $[75] = state.sessionId;
    $[76] = t29;
  } else {
    t29 = $[76];
  }
  let t30;
  if ($[80] !== state.phase || $[81] !== state.steps) {
    t30 = (state.phase === "starting" || state.phase === "thinking") && <Activity steps={state.steps} />;
    $[80] = state.phase;
    $[81] = state.steps;
    $[82] = t30;
  } else {
    t30 = $[82];
  }
  let t31;
  if ($[83] !== state.phase) {
    t31 = (state.phase === "posting" || state.phase === "composing") && <div className="flex items-center gap-2 px-1 text-sm text-textSecondary"><Sparkles className="size-4 animate-pulse text-primary" />{state.phase === "composing" ? "Writing your draft" : "Writing the post"}{" "}<Dots /></div>;
    $[83] = state.phase;
    $[84] = t31;
  } else {
    t31 = $[84];
  }
  let t32;
  if ($[85] !== contentUIRenderHandler || $[86] !== state.draft || $[87] !== state.phase) {
    t32 = state.phase === "editing" && state.draft && <div className="space-y-3 rounded-xl border border-ds-border-standard bg-background-light p-4"><div className="flex items-center gap-2 text-sm font-semibold text-textPrimary"><PenLine className="size-4 text-primary" />Edit your post</div><input value={state.draft.title} onChange={e_1 => dispatch({
        type: "EDIT_DRAFT",
        field: "title",
        value: e_1.target.value
      })} placeholder="Title" className="bg-background w-full rounded-lg border border-ds-border-standard px-3 py-2 text-sm font-semibold text-textPrimary outline-none focus:border-primary" /><textarea value={state.draft.body} onChange={e_2 => dispatch({
        type: "EDIT_DRAFT",
        field: "body",
        value: e_2.target.value
      })} rows={10} className="bg-background w-full resize-y rounded-lg border border-ds-border-standard px-3 py-2 font-mono text-xs leading-relaxed text-textPrimary outline-none focus:border-primary" /><div className="space-y-1"><p className="text-[11px] font-emphasis uppercase tracking-wider text-textSecondary">Preview</p><div className="rounded-lg border border-ds-border-subtle bg-background-primary px-3 py-2 text-sm">{contentUIRenderHandler(state.draft.body, "research-draft-preview", true, {
            hideLinkPreview: true
          })}</div></div><p className="text-xs text-textSecondary">Posts under your name, tagged with the AI model that helped research it.</p></div>;
    $[85] = contentUIRenderHandler;
    $[86] = state.draft;
    $[87] = state.phase;
    $[88] = t32;
  } else {
    t32 = $[88];
  }
  let t33;
  if ($[89] !== state.error || $[90] !== state.needsTrons) {
    t33 = state.error && !state.needsTrons && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{state.error}</p>;
    $[89] = state.error;
    $[90] = state.needsTrons;
    $[91] = t33;
  } else {
    t33 = $[91];
  }
  let t34;
  if ($[92] !== state.error || $[93] !== state.needsTrons) {
    t34 = state.error && state.needsTrons && <div className="flex flex-col gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between"><p className="text-textPrimary">{state.error}</p><Link href="/products" className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-primary px-5 py-2 font-emphasis text-white no-underline transition-all duration-300 hover:bg-indigo-400">Top up TRONs</Link></div>;
    $[92] = state.error;
    $[93] = state.needsTrons;
    $[94] = t34;
  } else {
    t34 = $[94];
  }
  let t35;
  if ($[95] !== state.phase || $[96] !== state.postCost || $[97] !== state.postUuid) {
    t35 = state.phase === "posted" && state.postUuid && <div className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm"><p className="text-textPrimary">Posted. This is now searchable on Factiii.{state.postCost != null && state.postCost > 0 && <span className="text-textSecondary">{" "}({state.postCost.toLocaleString()} Trons)</span>}</p><Link href={`/post/${state.postUuid}`} className="mt-1 inline-block font-emphasis text-primary hover:underline">View the post →</Link></div>;
    $[95] = state.phase;
    $[96] = state.postCost;
    $[97] = state.postUuid;
    $[98] = t35;
  } else {
    t35 = $[98];
  }
  let t36;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <div ref={bottomRef} />;
    $[99] = t36;
  } else {
    t36 = $[99];
  }
  let t37;
  if ($[100] !== t27 || $[101] !== t28 || $[102] !== t29 || $[103] !== t30 || $[104] !== t31 || $[105] !== t32 || $[106] !== t33 || $[107] !== t34 || $[108] !== t35) {
    t37 = <div className="space-y-3">{t27}{t28}{t29}{t30}{t31}{t32}{t33}{t34}{t35}{t36}</div>;
    $[100] = t27;
    $[101] = t28;
    $[102] = t29;
    $[103] = t30;
    $[104] = t31;
    $[105] = t32;
    $[106] = t33;
    $[107] = t34;
    $[108] = t35;
    $[109] = t37;
  } else {
    t37 = $[109];
  }
  const bodyInner = t37;
  let t38;
  if ($[110] !== confirmAsSelf || $[111] !== currentUser || $[112] !== state.draft?.body || $[113] !== state.phase) {
    t38 = state.phase === "editing" ? <div className="flex items-center gap-2"><Button variant="SECONDARY" text="Back" onClick={() => dispatch({
        type: "CANCEL_EDIT"
      })} /><div className="flex-1" /><Button variant="PRIMARY" text={`Post as @${currentUser.username}`} onClick={() => void confirmAsSelf()} disabled={!state.draft?.body.trim()} loading={state.phase !== "editing"} /></div> : null;
    $[110] = confirmAsSelf;
    $[111] = currentUser;
    $[112] = state.draft?.body;
    $[113] = state.phase;
    $[114] = t38;
  } else {
    t38 = $[114];
  }
  const editFooterInner = t38;
  let t39;
  if ($[115] !== busy || $[116] !== composeDraft || $[117] !== confirm || $[118] !== currentUser || $[119] !== onInput || $[120] !== send || $[121] !== skillChips || $[122] !== skillMenu || $[123] !== skillsButton || $[124] !== state.input || $[125] !== state.phase || $[126] !== state.premium || $[127] !== state.sessionId || $[128] !== vault) {
    t39 = state.phase !== "select" && state.phase !== "posted" && state.phase !== "editing" ? <div className="flex flex-col gap-2">{skillChips}<div ref={skillAnchor} className="relative flex items-center gap-2">{skillMenu}<input value={state.input} onChange={e_3 => onInput(e_3.target.value)} onKeyDown={e_4 => {
          if (e_4.key === "Enter" && !e_4.shiftKey) {
            e_4.preventDefault();
            send();
          }
        }} disabled={!state.sessionId || busy} placeholder={"Ask a follow-up or refine\u2026 (/ for skills)"} className="bg-background min-w-0 flex-1 rounded-full border border-ds-border-standard px-4 py-2.5 text-sm text-textPrimary outline-none focus:border-primary disabled:opacity-50" />{skillsButton}<Button variant="SECONDARY" onClick={() => void send()} disabled={!state.sessionId || busy || !state.input.trim()} icon={<Send size={18} />} /></div><Button variant="PRIMARY" text={vault ? "Post privately" : "Post as @factiii-bot"} onClick={() => void confirm()} disabled={!state.sessionId || busy || state.phase === "starting"} loading={state.phase === "posting"} /><p className="text-center text-xs text-textSecondary">{vault ? `Posts privately as @${currentUser.username}. Only you can see it, like your synced files.` : "Posts publicly under the bot\u2019s name and becomes searchable on Factiii."}</p>{state.premium && !vault && <Button variant="SECONDARY" text="Edit & post as me" icon={<PenLine size={16} />} onClick={() => void composeDraft()} disabled={!state.sessionId || busy || state.phase === "starting"} loading={state.phase === "composing"} />}</div> : null;
    $[115] = busy;
    $[116] = composeDraft;
    $[117] = confirm;
    $[118] = currentUser;
    $[119] = onInput;
    $[120] = send;
    $[121] = skillChips;
    $[122] = skillMenu;
    $[123] = skillsButton;
    $[124] = state.input;
    $[125] = state.phase;
    $[126] = state.premium;
    $[127] = state.sessionId;
    $[128] = vault;
    $[129] = t39;
  } else {
    t39 = $[129];
  }
  const composerInner = t39;
  if (inline) {
    let t40;
    if ($[130] !== handleClose || $[131] !== headerInner || $[132] !== hideHeader) {
      t40 = !hideHeader && <div className="flex shrink-0 items-center gap-2 border-b border-ds-border-subtle p-4"><div className="min-w-0 flex-1">{headerInner}</div><button type="button" onClick={handleClose} className="shrink-0 rounded-full px-3 py-1.5 text-sm font-semibold text-primary transition-colors hover:bg-hover">Done →</button></div>;
      $[130] = handleClose;
      $[131] = headerInner;
      $[132] = hideHeader;
      $[133] = t40;
    } else {
      t40 = $[133];
    }
    let t41;
    if ($[134] !== bodyInner) {
      t41 = <div className="min-h-0 flex-1 overflow-y-auto p-4">{bodyInner}</div>;
      $[134] = bodyInner;
      $[135] = t41;
    } else {
      t41 = $[135];
    }
    let t42;
    if ($[136] !== composerInner || $[137] !== editFooterInner) {
      t42 = editFooterInner || composerInner ? <div className="shrink-0 border-t border-ds-border-subtle p-3">{editFooterInner}{composerInner}</div> : null;
      $[136] = composerInner;
      $[137] = editFooterInner;
      $[138] = t42;
    } else {
      t42 = $[138];
    }
    let t43;
    if ($[139] !== t40 || $[140] !== t41 || $[141] !== t42) {
      t43 = <div className="flex h-full min-h-0 flex-col">{t40}{t41}{t42}</div>;
      $[139] = t40;
      $[140] = t41;
      $[141] = t42;
      $[142] = t43;
    } else {
      t43 = $[142];
    }
    return t43;
  }
  let t40;
  if ($[143] !== handleClose || $[144] !== headerInner) {
    t40 = <ModalHeader closeHandler={handleClose}>{headerInner}</ModalHeader>;
    $[143] = handleClose;
    $[144] = headerInner;
    $[145] = t40;
  } else {
    t40 = $[145];
  }
  let t41;
  if ($[146] !== bodyInner) {
    t41 = <ModalContent fillHeight={true}>{bodyInner}</ModalContent>;
    $[146] = bodyInner;
    $[147] = t41;
  } else {
    t41 = $[147];
  }
  let t42;
  if ($[148] !== editFooterInner) {
    t42 = editFooterInner && <ModalFooter>{editFooterInner}</ModalFooter>;
    $[148] = editFooterInner;
    $[149] = t42;
  } else {
    t42 = $[149];
  }
  let t43;
  if ($[150] !== composerInner) {
    t43 = composerInner && <ModalFooter>{composerInner}</ModalFooter>;
    $[150] = composerInner;
    $[151] = t43;
  } else {
    t43 = $[151];
  }
  let t44;
  if ($[152] !== handleClose || $[153] !== t40 || $[154] !== t41 || $[155] !== t42 || $[156] !== t43) {
    t44 = <Modal closeHandler={handleClose} maxWidth={620}>{t40}{t41}{t42}{t43}</Modal>;
    $[152] = handleClose;
    $[153] = t40;
    $[154] = t41;
    $[155] = t42;
    $[156] = t43;
    $[157] = t44;
  } else {
    t44 = $[157];
  }
  return t44;
};
export default ResearchPanel;
function _temp(q) {
  return {
    ...initialState,
    input: q.trim()
  };
}
function _temp2(p) {
  return p.uuid;
}
function _temp3(p_0) {
  return p_0.uuid;
}
function _temp4(sum, m) {
  return sum + (m.cost ?? 0);
}
function _temp5(k) {
  return k.title;
}